import * as React from "react";
import TextareaAutosize from "react-textarea-autosize";

import { cn } from "@src/lib/utils/cn";

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  autoSize?: boolean;
  minRows?: number;
  maxRows?: number;
  placeholderClassName?: string;
  /** Renders a <label> tied to the textarea, above it. */
  label?: string;
  /** Helper or error line under the textarea, linked by aria-describedby. */
  description?: string;
  /** Classes for the label/description wrapper (only rendered with either). */
  wrapperClassName?: string;
}

/**
 * The shared text-field look (keep in step with Input's field): rounded-lg,
 * a border-secondary hairline on bg-primary, 14px text with a text-tertiary
 * placeholder, and the 3px brand-border focus ring Button's main variants use.
 * aria-invalid switches the border and ring to status-error.
 */
const baseClassName =
  "flex min-h-[60px] w-full rounded-lg border border-border-secondary bg-bg-primary px-3 py-2 font-sans-v4 text-s-v4 font-normal-v4 text-text-primary transition-[color,border-color,box-shadow] placeholder:text-text-tertiary focus-visible:border-brand focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-brand-border disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-status-error-solid aria-invalid:focus-visible:border-status-error-solid aria-invalid:focus-visible:ring-status-error-border";

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      placeholderClassName,
      autoSize,
      minRows,
      maxRows,
      label,
      description,
      wrapperClassName,
      id,
      ...props
    },
    ref,
  ) => {
    const generatedId = React.useId();
    const textareaId = id || (label || description ? generatedId : undefined);
    const descriptionId = description
      ? `${generatedId}-description`
      : undefined;
    const invalid =
      props["aria-invalid"] === true || props["aria-invalid"] === "true";
    const describedBy =
      [props["aria-describedby"], descriptionId].filter(Boolean).join(" ") ||
      undefined;

    const classes = cn(baseClassName, className, placeholderClassName);
    const control = autoSize ? (
      <TextareaAutosize
        ref={ref}
        id={textareaId}
        className={classes}
        minRows={minRows}
        maxRows={maxRows}
        {...(props as React.ComponentProps<typeof TextareaAutosize>)}
        aria-describedby={describedBy}
      />
    ) : (
      <textarea
        ref={ref}
        id={textareaId}
        className={classes}
        {...props}
        aria-describedby={describedBy}
      />
    );

    if (!label && !description) {
      return control;
    }

    return (
      <div
        className={cn(
          "flex w-full flex-col gap-1.5",
          props.disabled && "cursor-not-allowed",
          wrapperClassName,
        )}
      >
        {label && (
          <label
            htmlFor={textareaId}
            className={cn(
              "font-sans-v4 text-xs-v4 font-medium-v4 text-text-primary",
              props.disabled && "opacity-50",
            )}
          >
            {label}
          </label>
        )}
        {control}
        {description && (
          <p
            id={descriptionId}
            className={cn(
              "font-sans-v4 text-2xs-v4 font-normal-v4 text-text-secondary",
              invalid && "text-status-error-text",
              props.disabled && "opacity-50",
            )}
          >
            {description}
          </p>
        )}
      </div>
    );
  },
);
Textarea.displayName = "Textarea";

export { Textarea };
